import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import classes from './dispatch-board-card.module.css'
import type {JobCardSummary, ResourceProgress} from './job-card-summary'
import {NotConfirmedTag, NoYardTag} from './terminal-row'

export type BoardCardVariant = 'card' | 'sticky'

type DispatchBoardCardProps = {
    summary: JobCardSummary
    variant?: BoardCardVariant
    highlighted?: boolean
    onPress: () => void
}

function ProgressPill({label, progress}: {label: string; progress: ResourceProgress}) {
    const stateClass = progress.complete ? classes['pillComplete'] : classes['pillOpen']
    return (
        <span className={`${classes['pill']} ${stateClass}`}>
            <span className={classes['pillCount']}>
                {progress.filled}/{progress.required}
            </span>
            <span className={classes['pillLabel']}>{label}</span>
        </span>
    )
}

/**
 * Shared job card for widescreen board layouts. `variant` only changes
 * visual density — the operational content is identical across layouts.
 */
export function DispatchBoardCard({
    summary,
    variant = 'card',
    highlighted = false,
    onPress,
}: DispatchBoardCardProps) {
    return (
        <button
            type="button"
            className={[
                classes['card'],
                variant === 'sticky' ? classes['cardSticky'] : classes['cardBlock'],
                classes[`lane_${summary.lane}` as keyof typeof classes],
                highlighted ? classes['highlighted'] : '',
            ]
                .filter(Boolean)
                .join(' ')}
            onClick={onPress}
        >
            <div className={classes['header']}>
                <span className={classes['title']}>{summary.title}</span>
                <div className={classes['scheduleBlock']}>
                    <div className={classes['scheduleAnchorGroup']}>
                        {summary.scheduleStatus ? (
                            <span className={classes['scheduleStatus']}>
                                {summary.scheduleStatus}
                            </span>
                        ) : null}
                        <span className={classes['schedule']}>{summary.scheduleAnchor}</span>
                    </div>
                    {summary.scheduleDuration ? (
                        <span
                            className={
                                summary.scheduleIsTime
                                    ? classes['scheduleTime']
                                    : classes['scheduleDuration']
                            }
                        >
                            {summary.scheduleDuration}
                        </span>
                    ) : null}
                </div>
            </div>

            <div className={classes['metaBlock']}>
                <div className={classes['metaLine']}>
                    <span className={classes['customer']}>{summary.customer}</span>
                    <span className={classes['metaSeparator']}> · </span>
                    <span className={classes['jobCode']}>{summary.jobCode}</span>
                </div>
                {summary.locationLine ? (
                    <div className={`${classes['metaLine']} ${classes['location']}`}>
                        {summary.locationLine}
                    </div>
                ) : null}
                {summary.outOfStateLabel ? (
                    <div className={classes['metaLine']}>
                        <OutOfStateBadge label={summary.outOfStateLabel} />
                    </div>
                ) : null}
                {summary.noYard ? (
                    <div className={classes['metaLine']}>
                        <NoYardTag />
                    </div>
                ) : null}
                {summary.notConfirmedCount ? (
                    <div className={classes['metaLine']}>
                        <NotConfirmedTag count={summary.notConfirmedCount} />
                    </div>
                ) : null}
                <div className={classes['metaLine']}>
                    Rep: {summary.internalContactName ?? 'Unassigned'}
                </div>
                {summary.crewLine ? (
                    <div className={`${classes['metaLine']} ${classes['crew']}`}>
                        {summary.crewLine}
                    </div>
                ) : null}
            </div>

            {summary.partialPickupLine ? (
                <div className={classes['partialPickup']} data-testid="partial-pickup-badge">
                    {summary.partialPickupLine}
                </div>
            ) : null}

            {(summary.drivers || summary.equipment) && (
                <div className={classes['progressRow']}>
                    {summary.drivers && <ProgressPill label="drivers" progress={summary.drivers} />}
                    {summary.equipment && (
                        <ProgressPill label="equip" progress={summary.equipment} />
                    )}
                </div>
            )}

            <div
                className={[
                    classes['status'],
                    classes[`tone_${summary.statusTone}`],
                    summary.lane === 'needs_action' && summary.statusTone === 'critical'
                        ? classes['actionCta']
                        : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {summary.statusLine}
            </div>
        </button>
    )
}
